import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, Globe, Eye, EyeOff, BookOpen } from 'lucide-react';

const TeacherAuthPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language, toggleLanguage, localize } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password);
      
      if (user.role !== 'teacher') {
        toast.error(localize({ ar: 'هذا الحساب ليس حساب أستاذ', en: 'This is not a teacher account' }));
        return;
      }
      
      toast.success(localize({ ar: 'تم تسجيل الدخول بنجاح', en: 'Login successful' }));
      navigate('/teacher');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail[language] || detail.ar : detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen gradient-hero flex items-center justify-center p-6 ${language === 'ar' ? 'rtl' : 'ltr'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/login" className="inline-flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <span className="text-2xl font-bold text-emerald-600">
                {localize({ ar: 'دخول الأستاذ', en: 'Teacher Login' })}
              </span>
              <span className="block text-xs text-slate-500">Learn With Trust</span>
            </div>
          </Link>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-end mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-slate-600 hover:text-emerald-600"
              >
                <Globe className="w-4 h-4 mr-1" />
                {language === 'ar' ? 'English' : 'العربية'}
              </Button>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              {localize({ ar: 'مرحباً بك', en: 'Welcome' })}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {localize({ ar: 'سجل دخولك لإدارة دروسك', en: 'Login to manage your lessons' })}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">
                  {localize({ ar: 'البريد الإلكتروني', en: 'Email' })}
                </Label>
                <div className="relative">
                  <Mail className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="example@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10' : 'pl-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500`}
                    required
                    data-testid="teacher-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">
                  {localize({ ar: 'كلمة المرور', en: 'Password' })}
                </Label>
                <div className="relative">
                  <Lock className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500`}
                    required
                    data-testid="teacher-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-emerald-600 hover:text-emerald-700"
                >
                  {localize({ ar: 'نسيت كلمة المرور؟', en: 'Forgot password?' })}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-glass btn-glass-emerald rounded-xl font-bold text-base tracking-wide"
                disabled={loading}
                data-testid="teacher-login-submit"
              >
                {loading 
                  ? (localize({ ar: 'جاري الدخول...', en: 'Logging in...' })) 
                  : (localize({ ar: 'دخول', en: 'Login' }))
                }
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                {localize({ ar: 'ليس لديك حساب؟', en: "Don't have an account?" })}{' '}
                <Link 
                  to="/register?role=teacher" 
                  className="text-emerald-600 hover:text-emerald-700 font-semibold"
                >
                  {localize({ ar: 'سجل كأستاذ', en: 'Register as Teacher' })}
                </Link>
              </p>
            </div>

            <div className="mt-4 p-4 bg-emerald-50 rounded-xl">
              <p className="text-sm text-emerald-700 text-center">
                {localize({ ar: '📚 انضم لمنصتنا وابدأ بتقديم الدروس الخصوصية للطلاب في الإمارات', en: '📚 Join our platform and start teaching students across the UAE' })
                }
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
          >
            <ArrowLeft className={`w-4 h-4 ${language === 'ar' ? '' : 'rotate-180'}`} />
            {localize({ ar: 'العودة لاختيار نوع الحساب', en: 'Back to account selection' })}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TeacherAuthPage;
